import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import * as productService from '../../services/productService';
import styles from './ProductsPage.module.css';

const ProductsPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [infoPopupProduct, setInfoPopupProduct] = useState(null);

  useEffect(() => {
    // Card 28286: a slug that names no category must render as NOT FOUND, never as an
    // empty category. Both requests answer 404 for an unknown slug, so a 404 from either
    // is the authority; any other failure stays a load error and keeps the old behaviour.
    const loadData = async () => {
      setLoading(true);
      setNotFound(false);
      setCategory(null);
      setProducts([]);
      try {
        const [categoryRes, productsRes] = await Promise.all([
          productService.getCategoryBySlug(slug),
          productService.getProductsByCategory(slug)
        ]);
        setCategory(categoryRes.category);
        setProducts(productsRes.products || []);
      } catch (error) {
        if (error?.status === 404) {
          setNotFound(true);
        } else {
          console.error('Failed to load data:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [slug]);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (notFound) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <nav className={styles.breadcrumb}>
            <Link to="/categories">Categories</Link>
            <span>/</span>
            <span>Not found</span>
          </nav>
          <div className={styles.empty}>
            <h3>Category not found</h3>
            <p>We could not find a category at this address. It may have been renamed or removed.</p>
            <Link to="/categories">
              <span className={styles.emptyBtn}>Browse Other Categories</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Breadcrumb */}
        <nav className={styles.breadcrumb}>
          <Link to="/categories">Categories</Link>
          <span>/</span>
          <span>{category?.name}</span>
        </nav>

        {/* Category Header */}
        <div className={styles.header}>
          <h1>{category?.name}</h1>
          <p>{category?.description}</p>
        </div>

        {/* Products Grid */}
        <div className={styles.grid}>
          {products.map((product) => (
            <div
              key={product.id}
              className={`${styles.productCard} ${product.isComingSoon ? styles.comingSoon : ''} ${product.isOutOfStock ? styles.outOfStock : ''}`}
            >
              <div
                className={styles.productImage}
                style={{ backgroundImage: `url(${product.image || '/images/product-placeholder.webp'})` }}
              >
                {product.isComingSoon && (
                  <div className={styles.comingSoonOverlay}>Coming Soon</div>
                )}
                {product.isOutOfStock && (
                  <div className={styles.outOfStockOverlay}>Out of Stock</div>
                )}
              </div>
              <div className={styles.productInfo}>
                <span className={styles.productCategory}>
                  {product.category?.name || category?.name || ''}
                </span>
                <h3 className={styles.productName}>{product.name}</h3>
                <p className={styles.productDescription}>
                  {product.shortDescription || product.description?.substring(0, 100)}
                </p>
                {product.isOutOfStock ? (
                  <div className={styles.outOfStockText}>Out of Stock</div>
                ) : product.startingAtPrice > 0 ? (
                  <div className={styles.productPrice}>
                    Starting at ${Math.ceil(product.startingAtPrice / 100)}/mo
                  </div>
                ) : null}
                <div className={styles.productActions}>
                  <button
                    className={styles.infoBtn}
                    onClick={() => setInfoPopupProduct(product)}
                  >
                    Info
                  </button>
                  {product.isComingSoon ? (
                    <button className={`${styles.requestBtn} ${styles.requestBtnDisabled}`} disabled>
                      Coming Soon
                    </button>
                  ) : product.isOutOfStock ? (
                    <button className={`${styles.requestBtn} ${styles.requestBtnDisabled}`} disabled>
                      Out of Stock
                    </button>
                  ) : (
                    <Link
                      to={`/intake/${product.slug || product.id}`}
                      className={styles.requestBtn}
                    >
                      Request
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {products.length === 0 && (
          <div className={styles.empty}>
            <h3>No products available</h3>
            <p>Check back soon for new treatments in this category.</p>
            <Link to="/categories">
              <span className={styles.emptyBtn}>Browse Other Categories</span>
            </Link>
          </div>
        )}
      </div>

      {/* Product Info Popup Modal */}
      {infoPopupProduct && createPortal(
        <div className={styles.popupOverlay} onClick={() => setInfoPopupProduct(null)}>
          <div className={styles.popupModal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.popupClose} onClick={() => setInfoPopupProduct(null)}>×</button>
            {(infoPopupProduct.lifestyleImageUrl || infoPopupProduct.primaryImage?.url || infoPopupProduct.image) && (
              <div className={styles.popupImageWrap}>
                <img
                  src={infoPopupProduct.lifestyleImageUrl || infoPopupProduct.primaryImage?.url || infoPopupProduct.image || '/images/product-placeholder.webp'}
                  alt={infoPopupProduct.name}
                  className={styles.popupImage}
                />
              </div>
            )}
            {/* Pricing */}
            {(() => {
              const ovalPrice = infoPopupProduct.startingAtPrice || (() => {
                const sorted = (infoPopupProduct.dosages || [])
                  .filter(d => d.price && d.price > 0)
                  .sort((a, b) => a.price - b.price);
                return sorted[0]?.price || 0;
              })();
              const cheapest = (infoPopupProduct.dosages || [])
                .filter(d => d.price && d.price > 0)
                .sort((a, b) => a.price - b.price)[0];
              const retailPrice = cheapest?.retailPrice || infoPopupProduct.retailPrice || 0;
              const savings = retailPrice > 0 && ovalPrice > 0 ? (retailPrice - ovalPrice) / 100 : 0;

              return ovalPrice > 0 ? (
                <div className={styles.popupPricing}>
                  <span className={styles.popupPricingLabel}>Starting at</span>
                  <div className={styles.popupPriceRow}>
                    {retailPrice > 0 && (
                      <span className={styles.popupRetailPrice}>
                        ${Math.ceil(retailPrice / 100)}
                      </span>
                    )}
                    <span className={styles.popupOvalPrice}>
                      ${Math.ceil(ovalPrice / 100)}
                    </span>
                  </div>
                  {savings > 0 && (
                    <span className={styles.popupSavings}>
                      Save ${Math.ceil(savings)}
                    </span>
                  )}
                </div>
              ) : null;
            })()}
            <div className={styles.popupBody}>
              <span className={styles.popupCategory}>
                {infoPopupProduct.category?.name || ''}
              </span>
              <h2 className={styles.popupTitle}>{infoPopupProduct.name}</h2>
              <div
                className={styles.popupDescription}
                dangerouslySetInnerHTML={{
                  __html: (infoPopupProduct.detailedDescription || infoPopupProduct.shortDescription || infoPopupProduct.description || 'No additional information available for this product.').replace(/&nbsp;/gi, ' ')
                }}
              />
              <Link
                to={`/intake/${infoPopupProduct.slug || infoPopupProduct.id}`}
                className={styles.popupCta}
                onClick={() => setInfoPopupProduct(null)}
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default ProductsPage;
